import { useState } from "react";
import { toast } from "sonner";
import { core } from "@/lib/mh";
import { errText, BACKEND } from "@/lib/http";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { StatusLight, ModeSwitch, Input, Btn, Copyable } from "@/components/mh/controls";

const GUIDE = [
  ["Get a Kimi key", "Create an API key at platform.kimi.ai (Moonshot). Keep it for OpenClaw and, optionally, for the AI model card above."],
  ["Install OpenClaw", "Run OpenClaw (Kimi Claw) on a server you control: Docker or a small VM is fine."],
  ["Add the Kimi provider", "In OpenClaw's provider settings, add Kimi / Moonshot with your key and pick the model your agents should use."],
  ["Turn on webhooks", "Point OpenClaw's outbound events at the Manahouse event address below and set the secret header X-Manahouse-Secret."],
  ["Give it a public address", "Expose OpenClaw over HTTPS (reverse proxy or tunnel) and create an access token for Manahouse."],
  ["Paste the details here", "Enter the address and token, save, then press Test connection."],
];

export const RuntimeCard = ({ data, onSaved, canEdit }) => {
  const r = data.runtime;
  const [mode, setMode] = useState(r.mode);
  const [url, setUrl] = useState(r.base_url || "");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const wrap = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const save = (patch) => wrap(async () => { onSaved((await core.setRuntime(patch)).data); setToken(""); toast.success("Runtime saved"); });
  const test = () => wrap(async () => {
    const res = (await core.testRuntime()).data;
    res.ok ? toast.success(res.detail || "OpenClaw answered. Connected.") : toast.error(res.error);
    onSaved((await core.connections()).data);
  });
  const status = mode === "managed" ? "managed" : r.mode === "own" ? r.status : "not_connected";
  const hook = `${BACKEND}/api/mh/webhooks/openclaw/${data.workspace_id}`;

  return (
    <Panel cls="p-5 sm:p-6 space-y-5" testId="runtime-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><Eyebrow>Agent runtime</Eyebrow><h3 className="font-display text-3xl mt-1">Where your agents actually run</h3></div>
        <StatusLight status={status} testId="runtime-status" />
      </div>
      <ModeSwitch testId="runtime-mode" value={mode} disabled={!canEdit || busy} onChange={(v) => { setMode(v); save({ mode: v }); }}
        options={[{ value: "managed", label: "Managed by Manahouse" }, { value: "own", label: "Client's own OpenClaw" }]} />
      {mode === "managed" ? (
        <p className="text-sm text-muted-foreground">Agents run on the Manahouse live simulation in this phase. Real hosted runtimes arrive in Phase 2. Your data stays inside this workspace.</p>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">Agents run on your OpenClaw / Kimi Claw. Activity streams into this dashboard through the event address below; your data stays in your systems.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="OpenClaw address" placeholder="https://claw.yourcompany.com" value={url} onChange={(e) => setUrl(e.target.value)} disabled={!canEdit} testId="runtime-url" />
            <Input label={r.token_mask ? `Access token (saved: ${r.token_mask})` : "Access token"} type="password" value={token} onChange={(e) => setToken(e.target.value)} disabled={!canEdit} testId="runtime-token" />
          </div>
          {r.error && r.mode === "own" && <p className="text-sm text-[#FF3B5C]" data-testid="runtime-error">{r.error}</p>}
          {r.standin && <p className="font-mono text-[11px] text-[#00E5FF]" data-testid="standin-badge">Using the Manahouse stand-in OpenClaw (for testing). Replace the address and token with your real gateway when ready.</p>}
          <div className="flex flex-wrap gap-2">
            <Btn testId="use-standin-btn" disabled={!canEdit || busy} onClick={() => wrap(async () => { const res = await core.useStandin(); setUrl(res.data.base_url); onSaved((await core.connections()).data); toast.success("Stand-in OpenClaw connected. Press Test connection"); })}>Use stand-in OpenClaw</Btn>
            <Btn variant="primary" testId="runtime-save-btn" disabled={!canEdit || busy} onClick={() => save({ mode: "own", base_url: url, token: token || undefined })}>Save</Btn>
            <Btn testId="runtime-test-btn" disabled={!canEdit || busy || !r.base_url} onClick={test}>Test connection</Btn>
          </div>
          <div className="grid gap-4 pt-2">
            <Copyable label="Manahouse event address (paste into OpenClaw webhooks)" value={hook} testId="webhook-url" onCopy={() => toast.success("Copied")} />
            {r.webhook_secret && <Copyable label="Secret header · X-Manahouse-Secret" value={r.webhook_secret} testId="webhook-secret" onCopy={() => toast.success("Copied")} />}
            {r.webhook_secret && <div><Btn testId="rotate-secret-btn" disabled={!canEdit || busy} onClick={() => wrap(async () => { onSaved((await core.rotateSecret()).data); toast.success("New secret generated"); })}>Rotate secret</Btn></div>}
            {r.last_event_at && <p className="font-mono text-[11px] text-[#D4FF3A]" data-testid="runtime-last-event">Last event received {new Date(r.last_event_at).toLocaleString()}</p>}
          </div>
          <ol className="grid sm:grid-cols-2 gap-3 pt-2" data-testid="setup-guide">
            {GUIDE.map(([t, d], i) => (
              <li key={t} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3.5">
                <div className="font-mono text-[10px] text-muted-foreground">STEP {i + 1}</div>
                <div className="text-sm font-medium mt-1">{t}</div>
                <p className="text-xs text-muted-foreground mt-1">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Panel>
  );
};
